import { useAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { ComponentProps, useCallback, useEffect } from 'react';

import { Alert, Modal, ModalBody, ModalFooter, ModalHeader } from '@growtherapy/sprout-ui';

import { logger } from '../../datadog/logger';
import { TrackingEvents, sendLoggingEvents } from '../../events';
import { useVideoPlatformCallback } from '../../hooks/useVideoPlatform';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { sessionInfoAtom } from '../../state';
import { toast } from '../../toasts';
import { normalizeVideoPlatform } from '../../video-sdk';
import {
  NewSessionConfirmationVariation,
  newSessionConfirmationDataAtom,
  providerShortIdAtom,
  waitingVisitorsAtom,
} from '../state';
import { getToastKeyForPatient } from '../useShowNewVisitorToastCallback';
import { useStartNewClientSessionCallback } from './useStartNewClientSessionCallback';

type StartNewSessionConfirmationModalProps = Pick<ComponentProps<typeof Modal>, 'onClose' | 'isOpen'> & {
  onConfirm: () => void;
  loading?: boolean;
  visitorName?: string;
  variation?: NewSessionConfirmationVariation;
};

export function StartNewSessionConfirmationModal({
  loading,
  onClose,
  onConfirm,
  visitorName,
  variation,
  ...props
}: StartNewSessionConfirmationModalProps) {
  return (
    <Modal {...{ onClose, ...props }}>
      <ModalHeader>
        <span className='fs-exclude' data-dd-privacy='mask'>
          Start a new session with {visitorName}?
        </span>
      </ModalHeader>
      <ModalBody>
        {variation === 'incompatible' && (
          <Alert className='mb-4' use='info' headingAs='h3'>
            This client is using an incompatible application version and cannot be admitted to the current session.
          </Alert>
        )}
        <span className='fs-exclude' data-dd-privacy='mask'>
          This will end your current session and create a new session with {visitorName}.
        </span>
      </ModalBody>
      <ModalFooter
        actions={[
          {
            key: 'cancel',
            use: 'secondary',
            onClick: onClose,
            actionContent: 'Cancel',
          },
          {
            key: 'confirm-new-session',
            use: 'primary',
            onClick: onConfirm,
            loading,
            actionContent: 'Yes, start new session',
          },
        ]}
      />
    </Modal>
  );
}

/**
 * Emits a Segment event when the user views the incompatible variation of the
 * new session confirmation modal.
 */
function useTrackIncompatibleVariationViewed(variation?: NewSessionConfirmationVariation, patientShortId?: string) {
  const { userViewed } = useTrackEvent();
  const getAppointmentShortId = useAtomCallback(useCallback((get) => get(sessionInfoAtom)?.appointment?.shortId, []));
  const getProviderShortId = useAtomCallback(useCallback((get) => get(providerShortIdAtom), []));
  const getVideoPlatform = useVideoPlatformCallback();
  const getWaitingVisitors = useAtomCallback(useCallback((get) => get(waitingVisitorsAtom), []));

  useEffect(
    function trackUserViewedIncompatibleVariation() {
      const providerShortId = getProviderShortId();
      const providerVideoPlatform = normalizeVideoPlatform(getVideoPlatform());
      if (variation === 'incompatible' && patientShortId && providerShortId) {
        logger.warn('Application compatibility issue detected', {
          clientVideoPlatforms: Object.values(getWaitingVisitors()).map((client) => client.videoPlatform),
          providerVideoPlatform,
        });
        userViewed(EventTypeEnum.VIDEO_PLATFORM_COMPATIBILITY_WARNING_VIEWED, {
          patientShortId,
          providerShortId: providerShortId,
          source: providerVideoPlatform,
          appointmentShortId: getAppointmentShortId(),
        });
      }
    },
    [
      userViewed,
      variation,
      patientShortId,
      getProviderShortId,
      getVideoPlatform,
      getAppointmentShortId,
      getWaitingVisitors,
    ],
  );
}

export function StartNewSessionConfirmationModalWrapper() {
  const [newSessionConfirmationData, setNewSessionConfirmationData] = useAtom(newSessionConfirmationDataAtom);
  const [onStartNew, { loading: isCompletingSession }] = useStartNewClientSessionCallback();
  const { visitorName, visitorUuid, patientShortId, variation } = newSessionConfirmationData ?? {};

  useTrackIncompatibleVariationViewed(variation, patientShortId);

  return (
    <StartNewSessionConfirmationModal
      isOpen={!!newSessionConfirmationData}
      loading={isCompletingSession}
      onClose={() => {
        setNewSessionConfirmationData(null);
      }}
      onConfirm={async () => {
        if (!visitorUuid || !visitorName || !patientShortId) return;
        await onStartNew(visitorUuid, visitorName, patientShortId);
        const toastKey = getToastKeyForPatient(patientShortId, visitorUuid);
        toast.close(toastKey);
        sendLoggingEvents(TrackingEvents.PROVIDER_END_AND_START_NEW_SESSION);
        setNewSessionConfirmationData(null);
      }}
      visitorName={visitorName}
      variation={variation}
    />
  );
}
